import { useGateValue } from '@coursera-web/statsig';

/**
 * Course Home Redesign rollout flag.
 *
 * Gate: https://console.statsig.com/2aQACzHI81tdbY41wEZm3H/gates/course-home-redesign
 *
 * ## E2E / local override
 *
 * Set `localStorage['course-home-redesign-override']` to `'true'` or `'false'` before
 * navigation to force legacy vs redesign UI on the same preview build. See
 * `integration/playwright/utils/courseHome.ts` (`setCourseHomeRedesignOverride`).
 *
 * ## Cleanup when legacy course-home is deleted (@cleanup course-home-redesign)
 *
 * Grep the repo for `@cleanup course-home-redesign` and:
 *
 * - Delete `integration/playwright/utils/courseHome.ts` and inline redesign-only selectors.
 * - Remove legacy branches in Playwright helpers (`locked-tooltip`, `> ul > li`, standalone
 *   Get started button, etc.).
 * - Drop temporary E2E-compat hooks added only for redesign parity (search: `E2E compat`,
 *   `legacy E2E`, `labelDataTestId`, `course-home-redesign-override`).
 * - Remove unit-test `useCourseHomeRedesign` mocks/cases that only exist to cover legacy paths.
 */

export const COURSE_HOME_REDESIGN_OVERRIDE_STORAGE_KEY = 'course-home-redesign-override';

const readCourseHomeRedesignOverride = (): boolean | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const value = window.localStorage.getItem(COURSE_HOME_REDESIGN_OVERRIDE_STORAGE_KEY);
    if (value === 'true') {
      return true;
    }
    if (value === 'false') {
      return false;
    }
    return null;
  } catch {
    return null;
  }
};

export const useCourseHomeRedesign = (): boolean => {
  const gateValue = useGateValue('course-home-redesign');
  const override = readCourseHomeRedesignOverride();

  if (override !== null) {
    return override;
  }

  return gateValue;
};
